// FE-SCRM-049 — AddProspectModal.
//
// Single-prospect create path. CSV bulk import lives at
// FE-SCRM-048 / Settings → Import data. On success, navigates to the
// new Prospect detail page (FE-SCRM-050's route).
//
// Field scope:
// * Required (per BE-SCRM-003 validation): name, renewal_month.
//   Renewal year is captured in the form and submitted whenever
//   the user supplies it, otherwise the BE defaults via
//   ``_default_renewal_year``.
// * Optional: EIN (9-digit), industry, address fields,
//   estimated_lives, current_broker_firm / office (with
//   autocomplete), relationship strength, source, referral source
//   pickers (conditional on source).
// * Tags: collected via FE-SCRM-060 ``<TagsInput>`` and submitted as
//   ``tags: [...]`` in the POST body. The BE's ``_sync_tags`` helper
//   reconciles them against the canonical ``AccountTag`` inventory +
//   ``SalesProspectTagLink`` join table (case-insensitive dedup;
//   auto-creates new tag names inline).
// * Owner picker: out of scope. ``SalesProspect`` has no owner_id
//   column today (owner / producer concept lives on
//   ``SalesOpportunity``).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Modal } from "@/design-system";

import {
  CrmFormBanner,
  TagsInput,
  TargetSearchPicker,
  type FormBannerError,
  type TargetSearchPickerProps,
  type TargetSearchResult,
} from "../components";

import {
  useBrokerFirmSuggestions,
  useCreateProspectMutation,
  useTagSuggestions,
  useTargetSearchResults,
} from "./hooks";
import {
  MONTH_LABEL,
  RELATIONSHIP_STRENGTH_LABEL,
  SOURCE_LABEL,
  VALID_SOURCES,
  type BrokerRelationshipStrength,
  type Prospect,
  type ProspectCreateInput,
  type ProspectSource,
} from "./types";


export interface AddProspectModalProps {
  open: boolean;
  onClose: () => void;
}


// EIN format per IRS spec: 9 digits, optional hyphen after the first
// two ("12-3456789"). The BE doesn't validate format (free-text
// column), so this client-side check is the only guardrail.
const EIN_REGEX = /^\d{2}-?\d{7}$/;


/** Format a partial EIN as the producer types: strip non-digits,
 * cap at 9 digits, insert a hyphen after the second digit so
 * "123456789" renders as "12-3456789". Empty input round-trips. */
function formatEin(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}


function defaultRenewalYear(month: number, today = new Date()): number {
  // Mirrors ``_default_renewal_year`` in backend/sales/prospect_views.py
  // so the form-level default lines up with what the BE would assign.
  const m = today.getUTCMonth() + 1;
  return month >= m ? today.getUTCFullYear() : today.getUTCFullYear() + 1;
}




export function AddProspectModal({
  open, onClose,
}: AddProspectModalProps): JSX.Element {
  const navigate = useNavigate();
  const create_mutation = useCreateProspectMutation();
  const fetchTags = useTagSuggestions();
  const fetchBrokerNames = useBrokerFirmSuggestions();
  const fetchSearch = useTargetSearchResults();

  // ── Form state ────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [ein, setEin] = useState("");
  const [renewal_month, setRenewalMonth] = useState<number>(
    new Date().getUTCMonth() + 1,
  );
  const [renewal_year, setRenewalYear] = useState<number>(
    defaultRenewalYear(new Date().getUTCMonth() + 1),
  );
  const [estimated_lives, setEstimatedLives] = useState<string>("");
  const [industry, setIndustry] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [postal_code, setPostalCode] = useState("");
  const [current_broker_firm, setCurrentBrokerFirm] = useState("");
  const [current_broker_office, setCurrentBrokerOffice] = useState("");
  const [
    current_broker_relationship_strength,
    setRelationshipStrength,
  ] = useState<BrokerRelationshipStrength>("unknown");
  const [source, setSource] = useState<ProspectSource>("other");
  const [tags, setTags] = useState<string[]>([]);
  const [
    referred_by_account, setReferredByAccount,
  ] = useState<TargetSearchResult | null>(null);
  const [
    referred_by_sales_contact, setReferredBySalesContact,
  ] = useState<TargetSearchResult | null>(null);
  const [
    referred_by_carrier_contact, setReferredByCarrierContact,
  ] = useState<TargetSearchResult | null>(null);

  // Broker-firm autocomplete: hand off to TagsInput's prop shape so
  // the producer gets the same UX as everywhere else (the firm input
  // doubles as a quick suggestion list).
  const [broker_suggestions, setBrokerSuggestions] = useState<string[]>([]);
  async function handleBrokerInputChange(next: string) {
    setCurrentBrokerFirm(next);
    if (!next.trim()) { setBrokerSuggestions([]); return; }
    try {
      const rows = await fetchBrokerNames(next.trim());
      setBrokerSuggestions(rows);
    } catch {
      setBrokerSuggestions([]);
    }
  }

  const [client_errors, setClientErrors] = useState<FormBannerError[]>([]);

  const banner_errors: FormBannerError[] = useMemo(() => {
    if (client_errors.length > 0) return client_errors;
    const err = create_mutation.error;
    if (!err) return [];
    // Duck-type the ApiError shape so test mocks that stamp
    // ``body.detail`` onto a plain Error round-trip the same as the
    // real api client's ApiError class.
    const body = (err as { body?: { detail?: unknown } }).body;
    const detail = typeof body?.detail === "string" ? body.detail : null;
    if (detail) {
      return [{ message: humanizeApiDetail(detail) }];
    }
    return [{ message: err.message ?? "Couldn't save the prospect." }];
  }, [client_errors, create_mutation.error]);

  function validate(): FormBannerError[] {
    const errors: FormBannerError[] = [];
    if (!name.trim()) {
      errors.push({ field: "name", message: "Prospect name is required." });
    }
    if (!Number.isInteger(renewal_month)
        || renewal_month < 1 || renewal_month > 12) {
      errors.push({
        field: "renewal_month",
        message: "Renewal month must be between 1 and 12.",
      });
    }
    if (ein.trim() && !EIN_REGEX.test(ein.trim())) {
      errors.push({
        field: "ein",
        message: "EIN must be 9 digits (e.g. 12-3456789).",
      });
    }
    if (estimated_lives.trim()) {
      const lives = Number.parseInt(estimated_lives, 10);
      if (!Number.isFinite(lives) || lives < 0) {
        errors.push({
          field: "estimated_lives",
          message: "Employee count must be a non-negative whole number.",
        });
      }
    }
    return errors;
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const errors = validate();
    setClientErrors(errors);
    if (errors.length > 0) return;

    const payload: ProspectCreateInput = {
      name: name.trim(),
      renewal_month,
    };
    if (Number.isFinite(renewal_year)) {
      payload.renewal_year = renewal_year;
    }
    if (ein.trim()) payload.ein = ein.trim().replace("-", "");
    if (industry.trim()) payload.industry = industry.trim();
    if (address.trim()) payload.address = address.trim();
    if (city.trim()) payload.city = city.trim();
    if (stateField.trim()) {
      payload.state = stateField.trim().toUpperCase();
    }
    if (postal_code.trim()) payload.postal_code = postal_code.trim();
    if (estimated_lives.trim()) {
      payload.estimated_lives = Number.parseInt(estimated_lives, 10);
    }
    if (current_broker_firm.trim()) {
      payload.current_broker_firm = current_broker_firm.trim();
    }
    if (current_broker_office.trim()) {
      payload.current_broker_office = current_broker_office.trim();
    }
    if (current_broker_relationship_strength !== "unknown") {
      payload.current_broker_relationship_strength =
        current_broker_relationship_strength;
    }
    if (source !== "other") payload.source = source;

    if (source === "referral") {
      if (referred_by_account) {
        payload.referred_by_account_id = referred_by_account.id;
      }
      if (referred_by_sales_contact) {
        payload.referred_by_sales_contact_id =
          referred_by_sales_contact.id;
      }
    } else if (source === "carrier_referral") {
      if (referred_by_carrier_contact) {
        payload.referred_by_carrier_contact_id =
          referred_by_carrier_contact.id;
      }
    }

    if (tags.length > 0) {
      payload.tags = tags;
    }

    create_mutation.mutate(payload, {
      onSuccess: (row: Prospect) => {
        navigate(`/dashboard/sales-crm/prospects/${row.prospect_id}`);
        onClose();
      },
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add prospect"
      size="lg"
    >
      <form
        data-testid="add-prospect-modal"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <CrmFormBanner errors={banner_errors} />

        {/* ── Required ───────────────────────────────────── */}
        <FieldGroup>
          <Field id="field-name" label="Prospect name" required>
            <input
              id="field-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Manufacturing"
              className={inputCls}
              autoFocus
            />
          </Field>
          <Field id="field-renewal_month" label="Renewal month" required>
            <select
              id="field-renewal_month"
              value={renewal_month}
              onChange={(e) =>
                setRenewalMonth(Number.parseInt(e.target.value, 10))
              }
              className={inputCls}
            >
              {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                <option key={m} value={m}>{MONTH_LABEL[m]}</option>
              ))}
            </select>
          </Field>
          <Field id="field-renewal_year" label="Renewal year" required>
            <input
              id="field-renewal_year"
              type="number"
              min={2020}
              max={2099}
              value={renewal_year}
              onChange={(e) =>
                setRenewalYear(Number.parseInt(e.target.value, 10))
              }
              className={inputCls}
            />
          </Field>
        </FieldGroup>

        {/* ── Identification ────────────────────────────── */}
        <FieldGroup>
          <Field id="field-ein" label="EIN">
            <input
              id="field-ein"
              type="text"
              inputMode="numeric"
              value={ein}
              onChange={(e) => setEin(formatEin(e.target.value))}
              placeholder="12-3456789"
              maxLength={10}
              className={inputCls}
            />
          </Field>
          <Field id="field-estimated_lives" label="Employee count">
            <input
              id="field-estimated_lives"
              type="number"
              min={0}
              value={estimated_lives}
              onChange={(e) => setEstimatedLives(e.target.value)}
              placeholder="e.g. 250"
              className={inputCls}
            />
          </Field>
          <Field id="field-industry" label="Industry">
            <input
              id="field-industry"
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="Manufacturing"
              className={inputCls}
            />
          </Field>
        </FieldGroup>

        {/* ── Address ───────────────────────────────────── */}
        <FieldGroup>
          <Field id="field-address" label="Address" wide>
            <input
              id="field-address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field id="field-city" label="City">
            <input
              id="field-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field id="field-state" label="State">
            <input
              id="field-state"
              type="text"
              maxLength={2}
              value={stateField}
              onChange={(e) => setStateField(e.target.value)}
              placeholder="CA"
              className={inputCls}
            />
          </Field>
          <Field id="field-postal_code" label="ZIP">
            <input
              id="field-postal_code"
              type="text"
              value={postal_code}
              onChange={(e) => setPostalCode(e.target.value)}
              className={inputCls}
            />
          </Field>
        </FieldGroup>

        {/* ── Current broker ────────────────────────────── */}
        <FieldGroup>
          <Field id="field-current_broker_firm" label="Current broker firm">
            <BrokerFirmAutocomplete
              value={current_broker_firm}
              onChange={handleBrokerInputChange}
              suggestions={broker_suggestions}
              onPick={(name) => {
                setCurrentBrokerFirm(name);
                setBrokerSuggestions([]);
              }}
            />
          </Field>
          <Field id="field-current_broker_office" label="Office / branch">
            <input
              id="field-current_broker_office"
              type="text"
              value={current_broker_office}
              onChange={(e) => setCurrentBrokerOffice(e.target.value)}
              placeholder="San Francisco"
              className={inputCls}
            />
          </Field>
          <Field id="field-relationship_strength" label="Relationship strength">
            <select
              id="field-relationship_strength"
              value={current_broker_relationship_strength}
              onChange={(e) =>
                setRelationshipStrength(
                  e.target.value as BrokerRelationshipStrength,
                )
              }
              className={inputCls}
            >
              {(["unknown", "weak", "strong"] as const).map((v) => (
                <option key={v} value={v}>
                  {RELATIONSHIP_STRENGTH_LABEL[v]}
                </option>
              ))}
            </select>
          </Field>
        </FieldGroup>

        {/* ── Source + referral pickers ────────────────── */}
        <FieldGroup>
          <Field id="field-source" label="Source">
            <select
              id="field-source"
              value={source}
              onChange={(e) =>
                setSource(e.target.value as ProspectSource)
              }
              className={inputCls}
            >
              {VALID_SOURCES.map((s) => (
                <option key={s} value={s}>{SOURCE_LABEL[s]}</option>
              ))}
            </select>
          </Field>
        </FieldGroup>

        {source === "referral" && (
          <FieldGroup>
            <Field
              id="field-referred_by_account"
              label="Referred by account"
              wide
            >
              <div data-testid="referred-by-account-picker">
                <TargetSearchPicker
                  value={referred_by_account}
                  onChange={setReferredByAccount}
                  fetchResults={fetchSearch as TargetSearchPickerProps["fetchResults"]}
                  restrictTo={["prospect"]}
                  placeholder="Search accounts…"
                />
              </div>
            </Field>
            <Field
              id="field-referred_by_sales_contact"
              label="Referred by sales contact"
              wide
            >
              <div data-testid="referred-by-sales-contact-picker">
                <TargetSearchPicker
                  value={referred_by_sales_contact}
                  onChange={setReferredBySalesContact}
                  fetchResults={fetchSearch as TargetSearchPickerProps["fetchResults"]}
                  restrictTo={["contact"]}
                  placeholder="Search sales contacts…"
                />
              </div>
            </Field>
          </FieldGroup>
        )}

        {source === "carrier_referral" && (
          <FieldGroup>
            <Field
              id="field-referred_by_carrier_contact"
              label="Referred by carrier contact"
              wide
            >
              <div data-testid="referred-by-carrier-contact-picker">
                <TargetSearchPicker
                  value={referred_by_carrier_contact}
                  onChange={setReferredByCarrierContact}
                  fetchResults={fetchSearch as TargetSearchPickerProps["fetchResults"]}
                  restrictTo={["carrier"]}
                  placeholder="Search carrier contacts…"
                />
              </div>
            </Field>
          </FieldGroup>
        )}

        {/* ── Tags ───────────────────────────────────── */}
        <FieldGroup>
          <Field id="field-tags" label="Tags" wide>
            <TagsInput
              value={tags}
              onChange={setTags}
              fetchSuggestions={fetchTags}
            />
          </Field>
        </FieldGroup>

        <footer className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            data-testid="add-prospect-submit"
            type="submit"
            disabled={create_mutation.isPending}
          >
            {create_mutation.isPending ? "Adding…" : "Add prospect"}
          </Button>
        </footer>
      </form>
    </Modal>
  );
}


// ── Local presentational helpers ─────────────────────────────────


// Shared input styling — mirrors the design-system ``<Input>``
// token set (bg-raised on the modal's bg-raised body, distinguished
// by the stronger border-default rim + teal focus). Aligns visually
// with FE-SCRM-051's AddOpportunityModal so the two add modals look
// like siblings.
const inputCls = [
  "w-full rounded-md border border-border-default bg-raised",
  "px-3 py-1.5 text-sm text-text-primary",
  "placeholder:text-text-tertiary outline-none",
  "transition-fast ease-out",
  "focus-visible:border-teal focus-visible:shadow-focus",
  "disabled:cursor-not-allowed disabled:opacity-60",
].join(" ");


function FieldGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {children}
    </div>
  );
}


function Field({
  id, label, children, required, wide,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
  required?: boolean;
  wide?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={
        "flex flex-col gap-1 text-xs font-medium text-gray-700 "
        + (wide ? "sm:col-span-3 " : "")
      }
    >
      <span>
        {label}
        {required && <span className="ml-0.5 text-rose-600">*</span>}
      </span>
      {children}
    </label>
  );
}


function BrokerFirmAutocomplete({
  value, onChange, suggestions, onPick,
}: {
  value: string;
  onChange: (next: string) => void;
  suggestions: string[];
  onPick: (name: string) => void;
}): JSX.Element {
  return (
    <div className="relative">
      <input
        id="field-current_broker_firm"
        type="text"
        value={value}
        onChange={(e) => void onChange(e.target.value)}
        placeholder="Mercer, Aon, Gallagher…"
        className={inputCls}
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <ul
          role="listbox"
          className={
            "absolute left-0 right-0 top-full z-20 mt-1 max-h-60 "
            + "overflow-auto rounded-md border border-border-default "
            + "bg-raised py-1 shadow-overlay"
          }
        >
          {suggestions.map((s) => (
            <li
              key={s}
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(s);
              }}
              className={
                "cursor-pointer px-3 py-1.5 text-sm "
                + "text-text-primary hover:bg-recessed"
              }
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function humanizeApiDetail(detail: string): string {
  switch (detail) {
    case "name_required":
      return "Prospect name is required.";
    case "renewal_month_required":
    case "invalid_renewal_month":
      return "Pick a renewal month between 1 and 12.";
    case "invalid_renewal_year":
      return "Renewal year is out of range.";
    case "invalid_estimated_lives":
      return "Employee count must be a non-negative whole number.";
    case "invalid_source":
      return "Pick a valid source.";
    case "invalid_relationship_strength":
      return "Pick a valid relationship strength.";
    case "invalid_referred_by_account_id":
      return "The referring account isn't in this entity.";
    case "invalid_referred_by_sales_contact_id":
      return "The referring sales contact isn't in this entity.";
    case "invalid_referred_by_carrier_contact_id":
      return "The referring carrier contact isn't recognized.";
    default:
      return `Couldn't save the prospect: ${detail}`;
  }
}
